import React, { useState } from 'react';
import ToolLayout from './layout/ToolLayout';
import Card, { CardHeader } from './ui/Card';
import Button from './ui/Button';
import CopyButton from './ui/CopyButton';
import FeedbackMessage from './ui/FeedbackMessage';
import FormField from './ui/FormField';

function decodeJwt(token) {
  const parts = token.trim().split('.');
  if (parts.length !== 3) throw new Error('Invalid JWT: expected 3 parts separated by dots');

  function base64UrlDecode(str) {
    const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '=='.slice(0, (4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  }

  return {
    header: base64UrlDecode(parts[0]),
    payload: base64UrlDecode(parts[1]),
  };
}

function buildExpiryNote(payloadObj) {
  if (payloadObj.exp === undefined) return '';
  const nowSec = Date.now() / 1000;
  if (payloadObj.exp < nowSec) return ' · Token expired';
  const diffSec = payloadObj.exp - nowSec;
  const diffDays = diffSec / 86400;
  if (diffDays >= 1) {
    return ` · Expires in ${Math.floor(diffDays)} day${Math.floor(diffDays) !== 1 ? 's' : ''}`;
  }
  const diffHours = diffSec / 3600;
  return ` · Expires in ${Math.floor(diffHours)} hour${Math.floor(diffHours) !== 1 ? 's' : ''}`;
}

const JwtDecoder = () => {
  const [input, setInput] = useState('');
  const [header, setHeader] = useState('');
  const [payload, setPayload] = useState('');
  const [feedback, setFeedback] = useState({ type: 'info', message: '' });

  const handleDecode = () => {
    if (!input.trim()) {
      setFeedback({ type: 'error', message: 'Paste a JWT token first.' });
      return;
    }

    try {
      const result = decodeJwt(input);
      const expiryNote = buildExpiryNote(result.payload);
      setHeader(JSON.stringify(result.header, null, 2));
      setPayload(JSON.stringify(result.payload, null, 2));
      setFeedback({ type: 'success', message: `Decoded successfully${expiryNote}` });
    } catch (e) {
      setHeader('');
      setPayload('');
      setFeedback({ type: 'error', message: e.message });
    }
  };

  return (
    <ToolLayout
      title="JWT Decoder"
      description="Decode JWT header and payload in the browser"
      category="Security & Identity"
      note="Never paste production secrets here — decoding is client-side only"
    >
      <Card>
        <CardHeader
          title="Token Input"
          description="Paste a JWT token to inspect its header and payload."
        />
        <FormField label="JWT Token">
          <textarea
            className="app-textarea"
            rows="3"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIn0.signature"
            spellCheck={false}
          />
        </FormField>
        <div className="tool-actions" style={{ marginBottom: 'var(--space-2)' }}>
          <Button onClick={handleDecode}>Decode</Button>
        </div>
        <FeedbackMessage type={feedback.type}>{feedback.message}</FeedbackMessage>
      </Card>

      <div className="tool-grid tool-grid--featured">
        <Card>
          <CardHeader
            title="Header"
            description="Algorithm and token type."
            action={<CopyButton value={header} label="Copy header" />}
          />
          <div className="output-panel">
            <span className="output-panel__label">Decoded header</span>
            <pre>{header || 'Header will appear here.'}</pre>
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Payload"
            description="Claims and data embedded in the token."
            action={<CopyButton value={payload} label="Copy payload" />}
          />
          <div className="output-panel">
            <span className="output-panel__label">Decoded payload</span>
            <pre>{payload || 'Payload will appear here.'}</pre>
          </div>
        </Card>
      </div>
    </ToolLayout>
  );
};

export default JwtDecoder;
